import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  getAgencyOverview,
  updateAgencyProfile,
  createAgencyLogoUploadUrl,
} from "@/lib/agency.functions";

const GOLD = "#C8A84B";

export const Route = createFileRoute("/agency/brand")({
  component: AgencyBrandPage,
});

function AgencyBrandPage() {
  const qc = useQueryClient();
  const fetchOverview = useServerFn(getAgencyOverview);
  const saveProfile = useServerFn(updateAgencyProfile);
  const initUpload = useServerFn(createAgencyLogoUploadUrl);

  const { data, isLoading } = useQuery({
    queryKey: ["agency-overview"],
    queryFn: () => fetchOverview(),
    staleTime: 30_000,
  });

  const [brandName, setBrandName] = useState("");
  const [brandColor, setBrandColor] = useState("#C8A84B");
  const [contactEmail, setContactEmail] = useState("");
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState<{ type: "ok" | "err"; text: string } | null>(
    null,
  );

  useEffect(() => {
    if (data?.profile) {
      setBrandName(data.profile.brand_name ?? "");
      setBrandColor(data.profile.brand_color ?? "#C8A84B");
      setContactEmail(data.profile.contact_email ?? "");
      setLogoUrl(data.profile.logo_url ?? null);
    }
  }, [data?.profile]);

  const handleLogo = async (file: File) => {
    setMsg(null);
    setUploading(true);
    try {
      const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const init = await initUpload({ data: { filename: safe } });
      if ("error" in init) throw new Error(init.error);
      const put = await fetch(init.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      if (!put.ok) throw new Error(`Upload failed (${put.status})`);
      setLogoUrl(init.publicUrl);
      setMsg({ type: "ok", text: "Logo uploaded. Don't forget to save." });
    } catch (e) {
      setMsg({ type: "err", text: e instanceof Error ? e.message : "Upload failed" });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(null);
    setSaving(true);
    try {
      const res = await saveProfile({
        data: {
          brand_name: brandName,
          brand_color: brandColor,
          contact_email: contactEmail,
          logo_url: logoUrl,
        },
      });
      if ("error" in res) throw new Error(res.error);
      setMsg({ type: "ok", text: "Brand settings saved." });
      qc.invalidateQueries({ queryKey: ["agency-overview"] });
    } catch (e) {
      setMsg({ type: "err", text: e instanceof Error ? e.message : "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="grid gap-8">
      <header>
        <p
          className="text-[10px] font-medium uppercase tracking-[0.22em]"
          style={{ color: GOLD }}
        >
          White-label
        </p>
        <h1 className="mt-2 font-display text-3xl font-semibold text-foreground">
          Brand settings
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Your logo, color, and brand name appear on every audit report you
          deliver. Contact email is where we send the final audit file.
        </p>
      </header>

      <form
        onSubmit={handleSubmit}
        className="grid gap-6 rounded-2xl border border-border bg-card/30 p-6"
      >
        <Field label="Agency name *">
          <input
            value={brandName}
            onChange={(e) => setBrandName(e.target.value)}
            required
            maxLength={80}
            placeholder="Acme Growth"
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
          />
        </Field>

        <Field label="Brand color (hex) *">
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={brandColor}
              onChange={(e) => setBrandColor(e.target.value)}
              className="h-10 w-14 cursor-pointer rounded-md border border-border bg-background"
            />
            <input
              value={brandColor}
              onChange={(e) => setBrandColor(e.target.value)}
              pattern="^#[0-9a-fA-F]{6}$"
              required
              className="w-32 rounded-lg border border-border bg-background px-3 py-2 font-mono text-sm text-foreground"
            />
          </div>
        </Field>

        <Field label="Contact email *">
          <input
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            required
            maxLength={255}
            placeholder="ops@youragency.com"
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
          />
        </Field>

        <Field label="Logo (PNG, JPG, SVG, or WebP)">
          <div className="flex flex-wrap items-center gap-4">
            {logoUrl && (
              <img
                src={logoUrl}
                alt="Logo preview"
                width={64}
                height={64}
                className="h-16 w-16 rounded-lg border border-border bg-background object-contain p-2"
              />
            )}
            <label className="inline-flex cursor-pointer items-center rounded-full border border-border bg-background px-4 py-2 text-xs font-medium uppercase tracking-[0.16em] text-foreground hover:bg-card">
              {uploading ? "Uploading…" : logoUrl ? "Replace logo" : "Upload logo"}
              <input
                type="file"
                accept=".png,.jpg,.jpeg,.svg,.webp,image/*"
                disabled={uploading}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleLogo(f);
                  e.target.value = "";
                }}
                className="hidden"
              />
            </label>
            {logoUrl && (
              <button
                type="button"
                onClick={() => setLogoUrl(null)}
                className="text-xs uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground"
              >
                Remove
              </button>
            )}
          </div>
        </Field>

        {msg && (
          <p
            className={`rounded-lg px-3 py-2 text-sm ${
              msg.type === "ok"
                ? "border border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                : "border border-red-500/30 bg-red-500/10 text-red-300"
            }`}
          >
            {msg.text}
          </p>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving || uploading}
            className="rounded-full bg-foreground px-6 py-2.5 text-sm font-medium text-background hover:opacity-90 disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save brand settings"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="grid gap-2 text-sm">
      <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}